const toBool = (value) => {
  const token = String(value || "").trim().toLowerCase();
  return token === "1" || token === "true" || token === "yes";
};

export const isSeedEnabled = () => toBool(process.env.REACT_APP_PARENT_SEED);

const pad2 = (value) => String(value).padStart(2, "0");

const toIsoDate = (value) => {
  const date = value instanceof Date ? value : new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const addDays = (base, offset) => {
  const date = base instanceof Date ? new Date(base.getTime()) : new Date(base || Date.now());
  date.setDate(date.getDate() + offset);
  return date;
};

const resolveClassSection = (params = {}) => {
  const className = String(
    params.className || params.class || params.classToken || params.classKey || "10"
  ).trim() || "10";
  const section = String(params.section || params.sectionName || "A").trim() || "A";
  return { className, section, classToken: `${className}-${section}` };
};

export const seedBoosterScore = (studentId = "") => {
  const score = 78;
  return {
    _seeded: true,
    available: true,
    studentId: String(studentId || "").trim() || undefined,
    score: {
      score,
      accuracy: 0.82,
      weeklyChange: 4,
    },
    trend: {
      value: 4,
      label: "this week",
    },
  };
};

export const seedCommunicationMessages = (params = {}) => {
  const { className, section, classToken } = resolveClassSection(params);
  const today = new Date();
  const todayIso = toIsoDate(today);
  const createdAt = today.toISOString();

  const homeworkMessages = [
    {
      _id: "seed-hw-1",
      type: "homework",
      status: "sent",
      homeworkClass: className,
      homeworkSection: section,
      homeworkSubject: "Mathematics",
      body: "Complete worksheet 3 (Q1-10).",
      date: todayIso,
      createdAt,
      updatedAt: createdAt,
      _seeded: true,
    },
    {
      _id: "seed-boost-1",
      status: "sent",
      subject: `[Booster] ${classToken}|Science`,
      body: "Revise chapter 4 and attempt the practice quiz.",
      date: todayIso,
      createdAt,
      updatedAt: createdAt,
      _seeded: true,
    },
  ];

  const coursePlanMessage = {
    _id: "seed-plan-1",
    status: "sent",
    subject: `Weekly Course Plan - ${classToken}`,
    body: [
      "Mathematics: Fractions and decimals recap.",
      "Science: Photosynthesis overview.",
      "English: Reading comprehension practice.",
      "Social Science: Maps and directions.",
    ].join("\n"),
    meta: {
      weekIndex: 0,
      subjectPlans: {
        Mathematics: "Fractions and decimals recap.",
        Science: "Photosynthesis overview.",
        English: "Reading comprehension practice.",
        "Social Science": "Maps and directions.",
      },
    },
    createdAt,
    updatedAt: createdAt,
    _seeded: true,
  };

  return {
    _seeded: true,
    messages: [...homeworkMessages, coursePlanMessage],
  };
};

export const seedTimetableByClass = (params = {}) => {
  const { classToken } = resolveClassSection(params);
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const slots = [
    { label: "Games", type: "Break", minutes: 45 },
    { label: "Prayer", type: "Break", minutes: 15 },
    { label: "Zero Period", type: "Break", minutes: 15 },
    { label: "Period 1", type: "Period", minutes: 35 },
    { label: "Fruits", type: "Break", minutes: 15 },
    { label: "Period 2", type: "Period", minutes: 35 },
    { label: "Period 3", type: "Period", minutes: 35 },
    { label: "H2O Break", type: "Break", minutes: 5 },
    { label: "Period 4", type: "Period", minutes: 35 },
    { label: "Lunch", type: "Break", minutes: 20 },
    { label: "Period 5", type: "Period", minutes: 35 },
    { label: "Period 6", type: "Period", minutes: 35 },
    { label: "Wind Up", type: "Break", minutes: 5 },
  ];

  const subjects = [
    { subject: "Mathematics", teacher: "Ms. Sharma" },
    { subject: "English", teacher: "Mr. Thomas" },
    { subject: "Science", teacher: "Ms. Khan" },
    { subject: "Social Science", teacher: "Mr. Gupta" },
    { subject: "Computer", teacher: "Ms. Iyer" },
    { subject: "Hindi", teacher: "Mrs. Verma" },
  ];

  const buildTimeRanges = () => {
    const ranges = [];
    let cursor = 8 * 60;
    slots.forEach((slot) => {
      const start = cursor;
      const end = cursor + slot.minutes;
      const toTime = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
      ranges.push({ startTime: toTime(start), endTime: toTime(end) });
      cursor = end;
    });
    return ranges;
  };

  const ranges = buildTimeRanges();
  const rows = Array.from({ length: slots.length }, (_, rowIndex) => {
    const range = ranges[rowIndex] || { startTime: "", endTime: "" };
    return days.map((day, dayIndex) => {
      const slot = slots[rowIndex];
      if (slot.type === "Break") {
        return {
          day,
          startTime: range.startTime,
          endTime: range.endTime,
          type: "Break",
          subject: slot.label,
          teacher: "",
          class: classToken,
          _seeded: true,
        };
      }
      const subjectIndex = (rowIndex + dayIndex) % subjects.length;
      const subjectSlot = subjects[subjectIndex];
      return {
        day,
        startTime: range.startTime,
        endTime: range.endTime,
        type: "Period",
        subject: subjectSlot.subject,
        teacher: subjectSlot.teacher,
        class: classToken,
        _seeded: true,
      };
    });
  });

  return {
    _seeded: true,
    rows,
    activeWeekNumber: 1,
  };
};

export const seedCalendarEvents = (params = {}) => {
  const today = new Date();
  const events = [
    {
      _id: "seed-cal-1",
      title: "Parent-Teacher Meeting",
      description: "Meet teachers to review progress.",
      category: "event",
      startDate: toIsoDate(addDays(today, 3)),
      endDate: toIsoDate(addDays(today, 3)),
      _seeded: true,
    },
    {
      _id: "seed-cal-2",
      title: "Science Exhibition",
      description: "Student projects showcase.",
      category: "celebration",
      startDate: toIsoDate(addDays(today, 10)),
      endDate: toIsoDate(addDays(today, 10)),
      _seeded: true,
    },
    {
      _id: "seed-cal-3",
      title: "Unit Test",
      description: "Syllabus: Chapters 1-4.",
      category: "exam",
      startDate: toIsoDate(addDays(today, 18)),
      endDate: toIsoDate(addDays(today, 18)),
      _seeded: true,
    },
  ];

  return {
    _seeded: true,
    params,
    events,
  };
};
